import React, { useState, useContext } from 'react';
import { AASTEPS } from '../../AA';
import { FlowType } from '../../../Utils/constants';
import { ROUTES } from '../../../Utils/routes';
import { AppBridge, AppContext } from '../../../App';
import styles from './index.module.css';
import { NudgeBody, NudgeCardProps } from '../NudgeStack/types';
import { GrowthComparisonChart, RateComparisonChart } from './charts';
import { pushEventToParent } from '../../../Utils/eventUtils';
import { captureException } from '../../../Utils/sentryUtils';
import { AssetCategory, isAccountOfCategory } from '../../../Utils/accountUtils';

enum JourneyType {
  BANK_ACCOUNTS = 'Internal_bank_accounts_journey',
  STOCK_ETF = 'Internal_stock_etf_journey',
  MUTUAL_FUNDS = 'Internal_mutual_funds_journey',
  BANK_ACCOUNT_DETAIL = 'Internal_bank_account_detail_journey',
}

const NudgeCard: React.FC<NudgeCardProps> = ({
  nudge,
  style,
  onLoadingChange,
}) => {
  const {
    setCurrentRoute,
    setCurrentFlowType,
    setAAInternalStep,
    statusData,
    data,
  }: any = useContext(AppContext);
  const { body } = nudge;

  const isStringBody = typeof body === 'string';
  const bodyObj: any = isStringBody ? body : (body as NudgeBody);

  const startAAJourney = (flowType: FlowType) => {
    setAAInternalStep(AASTEPS.AA_JOURNEY);
    setCurrentFlowType(flowType);
    setCurrentRoute(ROUTES.AAJOURNEY);
  };

  const hasActiveBankAccount = () =>
    !!statusData?.current?.accountDetails?.some(
      (account: any) =>
        account.status === 'ACTIVE' &&
        isAccountOfCategory(account.fiType, AssetCategory.BANKS)
    );

  const handleDeepLinkOpen = (e: any, link: any, enableLoading: any) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    try {
      pushEventToParent({
        eventCode: 'NUDGE_CTA_CLICK_' + nudge.category,
      });
    } catch (error) {
      captureException(error);
    }
    if (link.includes(JourneyType.BANK_ACCOUNTS)) {
      startAAJourney(FlowType.BANKS);
    } else if (link.includes(JourneyType.STOCK_ETF)) {
      startAAJourney(FlowType.STOCKS);
    } else if (link.includes(JourneyType.MUTUAL_FUNDS)) {
      startAAJourney(FlowType.MUTUAL_FUNDS);
    } else if (link.includes(JourneyType.BANK_ACCOUNT_DETAIL)) {
      if (!hasActiveBankAccount()) {
        startAAJourney(FlowType.BANKS);
      } else if (data?.current?.ACCOUNT_DETAILS?.accountProfileList?.length) {
        setCurrentRoute(ROUTES.JFS_BANK_ACCOUNT_DETAIL);
      }
    } else {
      if (enableLoading !== null && enableLoading === 'true') {
        onLoadingChange?.(true);
      }
      setTimeout(() => {
        window.open(link, '_self');
        onLoadingChange?.(false);
      }, 2000);
    }
  };

  return (
    <div className={styles.card} style={{ ...style }}>
      <div className={styles.content}>
        <div className={styles.headingBlock}>
          {(bodyObj?.productName || bodyObj?.badge) && (
            <div className={styles.productHeader}>
              {bodyObj?.productName && (
                <span className={styles.productName}>
                  {bodyObj?.productName}
                </span>
              )}
              {bodyObj?.badge && bodyObj.badge !== null && (
                <span className={styles.badge}>{bodyObj?.badge}</span>
              )}
            </div>
          )}

          <div className={styles.titleSection}>
            <h1 className={styles.title}>{bodyObj?.header}</h1>
          </div>
          {(() => {
            switch (bodyObj?.type) {
              case 'Regular schemes':
                return (
                  <>
                    {bodyObj?.body && (
                      <p className={styles.body}>{bodyObj.body}</p>
                    )}
                    <div className={styles.iconContainer}>
                      <div className={styles.sideIcon}>
                        <div className={styles.icon}>
                          {bodyObj?.leftIcon && (
                            <img
                              src={bodyObj.leftIcon}
                              alt=''
                              className={styles.icon}
                            />
                          )}
                        </div>
                        <div className={styles.name}>
                          {bodyObj?.leftIconName}
                        </div>
                      </div>

                      <div className={styles.middleIcon}>
                        <div className={styles.icon}>
                          {bodyObj?.middleIcon && (
                            <img
                              src={bodyObj.middleIcon}
                              alt=''
                              className={styles.icon}
                            />
                          )}
                        </div>
                        <div className={styles.name}>
                          {bodyObj?.middleIconName}
                        </div>
                      </div>

                      <div className={styles.sideIcon}>
                        <div className={styles.icon}>
                          {bodyObj?.rightIcon && (
                            <img
                              src={bodyObj.rightIcon}
                              alt=''
                              className={styles.icon}
                            />
                          )}
                        </div>
                        <div className={styles.name}>
                          {bodyObj?.rightIconName}
                        </div>
                      </div>
                    </div>
                  </>
                );

              case 'Info With Bullets':
                return (
                  <>
                    {bodyObj?.body && (
                      <p className={styles.body}>{bodyObj.body}</p>
                    )}

                    {bodyObj?.bulletsPointHeader && (
                      <div className={styles.benefitsHeader}>
                        {bodyObj.bulletsPointHeader}
                      </div>
                    )}

                    <div className={styles.bulletList}>
                      {bodyObj?.bulletsPoint1 && (
                        <div className={styles.bulletItem}>
                          <span className={styles.bulletText}>
                            {bodyObj.bulletsPoint1}
                          </span>
                        </div>
                      )}

                      {bodyObj?.bulletsPoint1 && bodyObj?.bulletsPoint2 && (
                        <div className={styles.divider}>
                          <div className={styles.strokeLine}></div>
                        </div>
                      )}

                      {bodyObj?.bulletsPoint2 && (
                        <div className={styles.bulletItem}>
                          <span className={styles.bulletText}>
                            {bodyObj.bulletsPoint2}
                          </span>
                        </div>
                      )}

                      {bodyObj?.bulletsPoint2 && bodyObj?.bulletsPoint3 && (
                        <div className={styles.divider}>
                          <div className={styles.strokeLine}></div>
                        </div>
                      )}

                      {bodyObj?.bulletsPoint3 && (
                        <div className={styles.bulletItem}>
                          <span className={styles.bulletText}>
                            {bodyObj.bulletsPoint3}
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                );

              case 'Growth Comparison':
                return (
                  <>
                    {bodyObj?.body && (
                      <p className={styles.description}>{bodyObj.body}</p>
                    )}
                    <GrowthComparisonChart series={bodyObj?.series ?? []} />
                  </>
                );

              case 'Rate Comparison':
                return (
                  <>
                    {bodyObj?.body && (
                      <p className={styles.description}>{bodyObj.body}</p>
                    )}
                    <RateComparisonChart
                      bars={bodyObj?.bars ?? []}
                      captions={bodyObj?.captions}
                      footnote={bodyObj?.footnote}
                    />
                  </>
                );

              case 'Content':
              default:
                return (
                  <>
                    {bodyObj?.body && (
                      <p className={styles.description}>{bodyObj.body}</p>
                    )}
                    {bodyObj?.footer && (
                      <p className={styles.title}>{bodyObj.footer}</p>
                    )}
                  </>
                );
            }
          })()}
        </div>
      </div>
      {(bodyObj?.appLink || bodyObj?.webLink) && (
        <div className={styles.actions}>
          {bodyObj.appLink && bodyObj.appText && (
            <button
              className={`${styles.button} ${styles.primaryButton}`}
              onTouchEnd={(e) => {
                handleDeepLinkOpen(
                  e,
                  bodyObj.appLink,
                  bodyObj?.enableAppLinkLoading
                );
              }}
              // Mouse has no touch events; touch taps never reach onClick
              // because the swipe card cancels touchstart
              onClick={(e) => {
                handleDeepLinkOpen(
                  e,
                  bodyObj.appLink,
                  bodyObj?.enableAppLinkLoading
                );
              }}
            >
              <span className={styles.buttonText}>{bodyObj.appText}</span>
            </button>
          )}

          {bodyObj.webLink && bodyObj.webText && (
            <button
              className={`${styles.button} ${styles.tertiaryButton}`}
              onTouchEnd={(e) => {
                handleDeepLinkOpen(
                  e,
                  bodyObj.webLink,
                  bodyObj?.enableWebLinkLoading
                );
              }}
              // Mouse has no touch events; touch taps never reach onClick
              // because the swipe card cancels touchstart
              onClick={(e) => {
                handleDeepLinkOpen(
                  e,
                  bodyObj.webLink,
                  bodyObj?.enableWebLinkLoading
                );
              }}
            >
              <span className={styles.buttonText}>{bodyObj.webText}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default NudgeCard;
